'use client'

import { useState } from 'react'
import { MapPin, Menu, ChevronDown, X, Clock, User, Trophy, Award, Gift, Check, Star, Crown, AlertCircle, Store, Zap, Shield } from 'lucide-react'

export default function Component() {
  const [page, setPage] = useState('home')
  const [selectedTask, setSelectedTask] = useState('')
  const [selectedSkills, setSelectedSkills] = useState([])
  const [isDropdownOpen, setIsDropdownOpen] = useState(false)
  const [isSkillsDropdownOpen, setIsSkillsDropdownOpen] = useState(false)
  const [price, setPrice] = useState('')
  const [range, setRange] = useState(5)
  const [customTask, setCustomTask] = useState('')
  const [showListings, setShowListings] = useState(false)
  const [timeframe, setTimeframe] = useState('Today')
  const [showRewardsModal, setShowRewardsModal] = useState(false)
  const [shopifyUrl, setShopifyUrl] = useState('')
  const [showShopifyModal, setShowShopifyModal] = useState(false)
  const [isProfileDropdownOpen, setIsProfileDropdownOpen] = useState(false)

  const tasks = ['Lawn Mowing', 'Shovelling Snow', 'Cleaning Pool', 'Cleaning Window', 'Gardening', 'Pet Care', 'Painting', 'Other']

  const mockListings = [
    {
      id: 1,
      title: 'Lawn Mowing',
      description: 'Need lawn mowing service for 1/4 acre yard',
      distance: '0.3 km',
      timePosted: '2h ago',
      price: 50,
      skill: 'Lawn Mowing',
    },
    {
      id: 2,
      title: 'Snow Shovelling',
      description: 'Looking for someone to clear driveway and walkway',
      distance: '0.8 km',
      timePosted: '1h ago',
      price: 45,
      skill: 'Shovelling Snow',
    },
    {
      id: 3,
      title: 'Window Cleaning',
      description: 'Need all exterior windows cleaned - 2 story house',
      distance: '1.2 km',
      timePosted: '30m ago',
      price: 75,
      skill: 'Cleaning Window',
    },
    {
      id: 4,
      title: 'Gardening',
      description: 'Need help with planting and maintaining a small vegetable garden',
      distance: '1.5 km',
      timePosted: '45m ago',
      price: 60,
      skill: 'Gardening',
    },
    {
      id: 5,
      title: 'Dog Walking',
      description: 'Looking for someone to walk my dog twice a day',
      distance: '0.7 km',
      timePosted: '1h ago',
      price: 40,
      skill: 'Pet Care',
    },
    {
      id: 6,
      title: 'House Painting',
      description: 'Need to paint the exterior of a small house',
      distance: '2.1 km',
      timePosted: '3h ago',
      price: 200,
      skill: 'Painting',
    },
  ]

  const timeframeData = {
    'Today': {
      jobs: 6,
      prevJobs: 4,
      profits: 350,
      prevProfits: 280,
      compareText: "today",
      tasks: [
        { name: 'Lawn Mowing', value: 45 },
        { name: 'Pool Cleaning', value: 30 },
        { name: 'Snow Shoveling', value: 5 },
        { name: 'Other', value: 20 }
      ]
    },
    'Yesterday': {
      jobs: 5,
      prevJobs: 6,
      profits: 280,
      prevProfits: 420,
      compareText: "yesterday",
      tasks: [
        { name: 'Lawn Mowing', value: 50 },
        { name: 'Pool Cleaning', value: 35 },
        { name: 'Snow Shoveling', value: 5 },
        { name: 'Other', value: 10 }
      ]
    },
    'This Week': {
      jobs: 35,
      prevJobs: 28,
      profits: 1850,
      prevProfits: 1200,
      compareText: "this week",
      tasks: [
        { name: 'Lawn Mowing', value: 40 },
        { name: 'Pool Cleaning', value: 35 },
        { name: 'Snow Shoveling', value: 10 },
        { name: 'Other', value: 15 }
      ]
    },
    'This Month': {
      jobs: 120,
      prevJobs: 98,
      profits: 6500,
      prevProfits: 5800,
      compareText: "this month",
      tasks: [
        { name: 'Lawn Mowing', value: 35 },
        { name: 'Pool Cleaning', value: 30 },
        { name: 'Snow Shoveling', value: 15 },
        { name: 'Other', value: 20 }
      ]
    }
  }

  const leaderboardData = [
    { name: 'Sarah Johnson', tasks: 156, rank: 1 },
    { name: 'Michael Chen', tasks: 143, rank: 2 },
    { name: 'Howard Smith', tasks: 128, rank: 3 },
    { name: 'Emma Davis', tasks: 115, rank: 4 },
    { name: 'James Wilson', tasks: 98, rank: 5 }
  ]

  const currentPrize = {
    name: "Professional Grade Lawn Mower",
    description: "Honda HRX217VKA 21-inch Gas Walk Behind Mower",
    value: "$799",
    image: "/placeholder.svg?height=200&width=200"
  }

  const premiumPerks = [
    { 
      id: 1, 
      name: "95% Commission Rate", 
      description: "Instead of next month's prize",
      icon: Crown
    },
    { 
      id: 2, 
      name: "15-Min Early Access", 
      description: "See and accept new tasks first",
      icon: Clock
    },
    { 
      id: 3, 
      name: "Extended Task Range", 
      description: "Access tasks from a wider area",
      icon: Star
    },
    { 
      id: 4, 
      name: "Elite Badge", 
      description: "Show your premium status",
      icon: Shield
    },
    { 
      id: 5, 
      name: "Instant Payout", 
      description: "Access earnings immediately",
      icon: Zap
    }
  ]

  const getFilteredListings = () => {
    if (selectedSkills.length === 0) {
      return []
    }
    return mockListings.filter((listing) => selectedSkills.includes(listing.skill))
  }

  const handleSkillToggle = (skill) => {
    if (skill === 'Select All') {
      if (selectedSkills.length === tasks.length - 1) {
        setSelectedSkills([])
      } else {
        setSelectedSkills([...tasks.filter((t) => t !== 'Other')])
      }
    } else {
      if (selectedSkills.includes(skill)) {
        setSelectedSkills(selectedSkills.filter((s) => s !== skill))
      } else {
        setSelectedSkills([...selectedSkills, skill])
      }
    }
  }

  const calculatePercentageChange = (current, previous) => {
    const change = ((current - previous) / previous) * 100
    return change.toFixed(0)
  }

  const DemoMap = ({ range }) => {
    const offsetX = Math.sin(Date.now() / 10000) * 20
    const offsetY = Math.cos(Date.now() / 10000) * 20

    return (
      <div className="relative w-full h-48 bg-gray-800 rounded-lg overflow-hidden mt-4">
        <div className="absolute inset-0 opacity-50">
          <svg width="100%" height="100%" xmlns="http://www.w3.org/2000/svg">
            <defs>
              <pattern id="grid" width="20" height="20" patternUnits="userSpaceOnUse">
                <path d="M 20 0 L 0 0 0 20" fill="none" stroke="rgba(255,255,255,0.1)" strokeWidth="1" />
              </pattern>
            </defs>
            <rect width="100%" height="100%" fill="url(#grid)" />
          </svg>
        </div>
        <div
          className="absolute transform -translate-x-1/2 -translate-y-1/2 transition-all duration-300"
          style={{
            left: `calc(50% + ${offsetX}px)`,
            top: `calc(50% + ${offsetY}px)`,
          }}
        >
          <div className="w-4 h-4 bg-red-500 rounded-full shadow-lg" />
          <div
            className="absolute left-1/2 top-1/2 transform -translate-x-1/2 -translate-y-1/2 border-2 border-red-500 rounded-full opacity-30"
            style={{
              width: `${range * 10}px`,
              height: `${range * 10}px`,
              transition: 'all 0.3s ease',
            }}
          />
        </div>
      </div>
    )
  }

  const TaskListing = ({ task }) => (
    <div
      className="bg-white rounded-xl shadow-sm p-6 space-y-2 transition-transform hover:scale-[1.02] cursor-pointer"
      onClick={() => setPage('membership')}
    >
      <div className="flex justify-between items-start">
        <h3 className="text-2xl font-bold">{task.title}</h3>
        <span className="text-2xl font-bold">${task.price}</span>
      </div>
      <p className="text-gray-600">{task.description}</p>
      <div className="flex items-center gap-4 text-gray-500">
        <div className="flex items-center gap-1">
          <MapPin className="w-4 h-4" />
          <span>{task.distance}</span>
        </div>
        <div className="flex items-center gap-1">
          <Clock className="w-4 h-4" />
          <span>{task.timePosted}</span>
        </div>
      </div>
    </div>
  )

  const RewardsModal = () => (
    <div className={`fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center ${showRewardsModal ? '' : 'hidden'}`}>
      <div className="bg-white rounded-xl p-4 sm:p-8 max-w-2xl w-full mx-4 max-h-[90vh] overflow-y-auto">
        <div className="flex justify-between items-center mb-6">
          <h2 className="text-xl sm:text-2xl font-bold">Monthly Rewards Competition</h2>
          <button onClick={() => setShowRewardsModal(false)} className="text-gray-500 hover:text-gray-700">
            <X className="w-6 h-6" />
          </button>
        </div>

        <div className="mb-8">
          <div className="flex items-center gap-4 mb-4">
            <Trophy className="w-6 h-6 sm:w-8 sm:h-8 text-yellow-500" />
            <h3 className="text-lg sm:text-xl font-semibold">This Month's Prize</h3>
          </div>
          <div className="bg-gray-50 rounded-lg p-4 flex flex-col sm:flex-row gap-4">
            <img src={currentPrize.image} alt={currentPrize.name} className="w-full sm:w-32 h-32 rounded-lg object-cover" />
            <div>
              <h4 className="font-bold text-lg">{currentPrize.name}</h4>
              <p className="text-gray-600 mb-2">{currentPrize.description}</p>
              <p className="text-green-600 font-bold">Value: {currentPrize.value}</p>
            </div>
          </div>
        </div>

        <div className="mb-6">
          <div className="flex items-center gap-4 mb-4">
            <Award className="w-6 h-6 sm:w-8 sm:h-8 text-blue-500" />
            <h3 className="text-lg sm:text-xl font-semibold">Current Leaderboard</h3>
          </div>
          <div className="space-y-4">
            {leaderboardData.map((person) => (
              <div key={person.name} className="flex items-center justify-between bg-gray-50 p-4 rounded-lg">
                <div className="flex items-center gap-4">
                  <span className={`w-8 h-8 flex items-center justify-center rounded-full ${
                    person.rank === 1 ? 'bg-yellow-100 text-yellow-700' :
                    person.rank === 2 ? 'bg-gray-100 text-gray-700' :
                    person.rank === 3 ? 'bg-orange-100 text-orange-700' :
                    'bg-blue-50 text-blue-700'
                  }`}>
                    {person.rank}
                  </span>
                  <span className="font-medium">{person.name}</span>
                </div>
                <span className="font-bold">{person.tasks} tasks</span>
              </div>
            ))}
          </div>
        </div>

        <div className="text-center text-sm text-gray-500">
          Competition ends in 15 days. Winner will be announced on the 1st of next month.
        </div>
      </div>
    </div>
  )

  const ShopifyModal = () => (
    <div className={`fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 ${showShopifyModal ? '' : 'hidden'}`}>
      <div className="bg-white rounded-2xl p-8 max-w-md w-full mx-4 shadow-2xl">
        <h3 className="text-2xl font-bold mb-4">Connect to Shopify Store</h3>
        <p className="text-gray-600 mb-6">Link these plans to your Shopify pricing page</p>
        
        <input
          type="url"
          placeholder="Your Shopify pricing page URL"
          value={shopifyUrl}
          onChange={(e) => setShopifyUrl(e.target.value)}
          className="w-full p-3 border border-gray-300 rounded-lg mb-4 focus:ring-2 focus:ring-blue-500 focus:border-transparent"
        />
        
        <div className="flex gap-3">
          <button 
            onClick={() => setShowShopifyModal(false)}
            className="flex-1 py-3 px-4 border border-gray-300 rounded-lg hover:bg-gray-100 transition-colors"
          >
            Cancel
          </button>
          <button 
            onClick={() => {
              setShowShopifyModal(false);
            }}
            className="flex-1 py-3 px-4 bg-blue-500 text-white rounded-lg hover:bg-blue-600 transition-colors"
          >
            Connect Store
          </button>
        </div>
      </div>
    </div>
  )

  return (
    <div className="min-h-screen bg-gray-50">
      <header className="p-4 flex flex-col sm:flex-row sm:items-center justify-between border-b bg-white">
        <div className="text-2xl font-bold mb-4 sm:mb-0">LUVO</div>
        <div className="flex flex-wrap gap-2 sm:gap-4 items-center">
          <button
            onClick={() => {
              setPage('help')
              setShowListings(false)
            }}
            className={`px-4 sm:px-6 py-2 rounded-full text-sm sm:text-base ${page === 'help' ? 'bg-black text-white' : 'bg-gray-100'}`}
          >
            Looking to Help
          </button>
          <button
            onClick={() => {
              setPage('need')
              setShowListings(false)
            }}
            className={`px-4 sm:px-6 py-2 rounded-full text-sm sm:text-base ${
              page === 'need' || page === 'home' ? 'bg-black text-white' : 'bg-gray-100'
            }`}
          >
            Need Help
          </button>
          <div className="relative ml-auto sm:ml-0">
            <button
              onClick={() => setIsProfileDropdownOpen(!isProfileDropdownOpen)}
              className="p-2 flex items-center gap-2 rounded-full hover:bg-gray-100"
            >
              <User className="w-6 h-6" />
              <ChevronDown className={`w-4 h-4 transition-transform ${isProfileDropdownOpen ? 'rotate-180' : ''}`} />
            </button>
            {isProfileDropdownOpen && (
              <div className="absolute right-0 mt-2 w-48 bg-white rounded-md shadow-lg py-1 z-10">
                <button
                  onClick={() => {
                    setPage('dashboard')
                    setIsProfileDropdownOpen(false)
                  }}
                  className="block px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 w-full text-left"
                >
                  Dashboard
                </button>
                <button
                  onClick={() => {
                    setPage('membership')
                    setIsProfileDropdownOpen(false)
                  }}
                  className="block px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 w-full text-left"
                >
                  Membership
                </button>
              </div>
            )}
          </div>
        </div>
      </header>

      <main className="p-4 md:p-6 max-w-4xl mx-auto">
        {page === 'dashboard' && (
          <div className="p-4 sm:p-6 max-w-4xl mx-auto bg-gray-50">
            <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center mb-8">
              <h1 className="text-3xl sm:text-4xl font-bold mb-4 sm:mb-0">Welcome back, Howard</h1>
              <button 
                onClick={() => setShowRewardsModal(true)}
                className="flex items-center gap-2 bg-gradient-to-r from-purple-600 to-blue-600 text-white px-4 py-2 rounded-lg hover:opacity-90 transition-opacity"
              >
                <Gift className="w-5 h-5" />
                <span>Rewards Program</span>
              </button>
            </div>
            
            {/* Time Period Filters */}
            <div className="flex flex-wrap gap-2 sm:gap-3 mb-8">
              {Object.keys(timeframeData).map(period => (
                <button 
                  key={period}
                  onClick={() => setTimeframe(period)}
                  className={`px-3 sm:px-4 py-2 rounded-full transition-colors text-sm sm:text-base ${
                    timeframe === period 
                      ? 'bg-black text-white' 
                      : 'bg-white hover:bg-gray-100'
                  }`}
                >
                  {period}
                </button>
              ))}
            </div>

            {/* Metrics Cards */}
            <div className="grid gap-4 mb-8">
              {/* Total Jobs Card */}
              <div className="bg-white p-4 sm:p-6 rounded-xl">
                <h3 className="text-gray-600 mb-4">Total Jobs</h3>
                <div className="flex items-center gap-3 mb-2">
                  <span className="text-3xl sm:text-5xl font-bold">{timeframeData[timeframe].jobs}</span>
                  <span className={`px-2 py-1 rounded-full text-sm ${
                    parseInt(calculatePercentageChange(timeframeData[timeframe].jobs, timeframeData[timeframe].prevJobs)) >= 0 
                      ? 'bg-green-100 text-green-800' 
                      : 'bg-red-100 text-red-800'
                  }`}>
                    {parseInt(calculatePercentageChange(timeframeData[timeframe].jobs, timeframeData[timeframe].prevJobs)) >= 0 ? '↑' : '↓'}
                    {Math.abs(calculatePercentageChange(timeframeData[timeframe].jobs, timeframeData[timeframe].prevJobs))}%
                  </span>
                </div>
                <p className="text-gray-500">
                  vs previous {timeframeData[timeframe].compareText}: {timeframeData[timeframe].prevJobs}
                </p>
              </div>

              {/* Profits Card */}
              <div className="bg-white p-4 sm:p-6 rounded-xl">
                <h3 className="text-gray-600 mb-4">Profits</h3>
                <div className="flex items-center gap-3 mb-2">
                  <span className="text-3xl sm:text-5xl font-bold">${timeframeData[timeframe].profits}</span>
                  <span className={`px-2 py-1 rounded-full text-sm ${
                    parseInt(calculatePercentageChange(timeframeData[timeframe].profits, timeframeData[timeframe].prevProfits)) >= 0 
                      ? 'bg-green-100 text-green-800' 
                      : 'bg-red-100 text-red-800'
                  }`}>
                    {parseInt(calculatePercentageChange(timeframeData[timeframe].profits, timeframeData[timeframe].prevProfits)) >= 0 ? '↑' : '↓'}
                    {Math.abs(calculatePercentageChange(timeframeData[timeframe].profits, timeframeData[timeframe].prevProfits))}%
                  </span>
                </div>
                <p className="text-gray-500">
                  vs previous {timeframeData[timeframe].compareText}: ${timeframeData[timeframe].prevProfits}
                </p>
              </div>
            </div>

            {/* Tasks Completed Section */}
            <div className="bg-white p-4 sm:p-6 rounded-xl">
              <h3 className="text-xl font-bold mb-6">Tasks Completed</h3>
              <div className="space-y-6">
                {timeframeData[timeframe].tasks.map(task => (
                  <div key={task.name}>
                    <div className="flex justify-between mb-2">
                      <span className="text-sm sm:text-lg">{task.name}</span>
                      <span className="text-sm sm:text-lg">{task.value}%</span>
                    </div>
                    <div className="h-2 bg-gray-100 rounded-full overflow-hidden">
                      <div 
                        className="h-full bg-gray-300 transition-all duration-300"
                        style={{ width: `${task.value}%` }}
                      />
                    </div>
                  </div>
                ))}
              </div>
            </div>

            <RewardsModal />
          </div>
        )}

        {(page === 'home' || page === 'need') && (
          <div className="space-y-8">
            <h1 className="text-3xl md:text-5xl font-bold">
              Get Local Help
              <br className="hidden md:inline" />
              In <span className="text-red-500">Minutes</span>
            </h1>
            <p className="text-lg md:text-xl">Request a Job, Set Your Price, & Get Help</p>

            <div className="space-y-4">
              <div className="bg-black text-gray-300 p-4 rounded-lg">
                <label className="block mb-2">Enter Task</label>
                <div className="relative">
                  <button onClick={() => setIsDropdownOpen(!isDropdownOpen)} className="w-full text-left flex justify-between items-center">
                    {selectedTask || 'Select a task'}
                    <ChevronDown className={`w-5 h-5 transition-transform ${isDropdownOpen ? 'rotate-180' : ''}`} />
                  </button>

                  {isDropdownOpen && (
                    <div className="absolute top-full left-0 right-0 mt-1 bg-black border border-gray-700 rounded-lg py-2 z-10">
                      {tasks.map((task) => (
                        <button
                          key={task}
                          className="w-full text-left px-4 py-2 hover:bg-gray-800"
                          onClick={() => {
                            setSelectedTask(task)
                            setIsDropdownOpen(false)
                            if (task === 'Other') {
                              setCustomTask('')
                            }
                          }}
                        >
                          {task}
                        </button>
                      ))}
                    </div>
                  )}
                </div>
                {selectedTask && (
                  <div className="mt-4">
                    <label className="block mb-2">Additional Info</label>
                    <textarea
                      placeholder="Provide any additional details about the task"
                      className="w-full h-24 bg-gray-800 border border-gray-700 rounded-lg p-2 text-white"
                    />
                  </div>
                )}
                {selectedTask === 'Other' && (
                  <input
                    type="text"
                    placeholder="Specify your task"
                    value={customTask}
                    onChange={(e) => setCustomTask(e.target.value)}
                    className="bg-transparent border-b w-full mt-4"
                  />
                )}
              </div>

              <div className="bg-black text-gray-300 p-4 rounded-lg">
                <label className="block mb-2">Set Price</label>
                <input
                  type="number"
                  value={price}
                  onChange={(e) => setPrice(e.target.value)}
                  className="bg-transparent border-b w-full"
                  placeholder="Enter your price"
                />
              </div>

              <button className="bg-black text-white px-8 py-3 rounded-lg w-32 text-lg">Get Help</button>
            </div>

            <div className="text-center mt-12">
              <div className="flex items-center justify-center gap-4">
                <div className="w-8 h-8 bg-green-500 rounded-lg" />
                <h2 className="text-xl md:text-2xl font-bold">Safe & Secure</h2>
                <div className="w-8 h-8 bg-green-500 rounded-lg" />
              </div>
              <p className="mt-2">Verified Workers & Mandatory Reviews</p>
            </div>
          </div>
        )}

        {page === 'help' && (
          <div className="space-y-8">
            <h1 className="text-3xl md:text-5xl font-bold">
              Find <span className="text-red-500">Tasks</span> in your
              <br />
              Community
            </h1>
            <p className="text-lg md:text-xl">Select Your Skills, Range, and Make Money</p>

            <div className="space-y-4">
              <div className="bg-black text-gray-300 p-4 rounded-lg">
                <label className="block mb-2">Enter Range</label>
                <div className="space-y-4">
                  <input type="range" min="0" max="20" value={range} onChange={(e) => setRange(e.target.value)} className="w-full" />
                  <div className="flex items-center gap-2">
                    <MapPin className="w-5 h-5" />
                    <span>{range}km radius</span>
                  </div>
                  <DemoMap range={range} />
                </div>
              </div>

              <div className="bg-black text-gray-300 p-4 rounded-lg">
                <label className="block mb-2">Set Skills</label>
                <div className="relative">
                  <button
                    onClick={() => setIsSkillsDropdownOpen(!isSkillsDropdownOpen)}
                    className="w-full text-left flex justify-between items-center"
                  >
                    <div className="flex flex-wrap gap-2">
                      {selectedSkills.length > 0 ? (
                        selectedSkills.map((skill) => (
                          <span key={skill} className="bg-gray-700 px-2 py-1 rounded-full text-sm flex items-center gap-1">
                            {skill}
                            <button
                              onClick={(e) => {
                                e.stopPropagation()
                                handleSkillToggle(skill)
                              }}
                            >
                              <X className="w-4 h-4" />
                            </button>
                          </span>
                        ))
                      ) : (
                        'Select your skills'
                      )}
                    </div>
                    <ChevronDown className={`w-5 h-5 transition-transform ${isSkillsDropdownOpen ? 'rotate-180' : ''}`} />
                  </button>

                  {isSkillsDropdownOpen && (
                    <div className="absolute top-full left-0 right-0 mt-1 bg-black border border-gray-700 rounded-lg py-2 z-10">
                      <div className="px-4 py-2 border-b border-gray-700">
                        <label className="flex items-center gap-2">
                          <input
                            type="checkbox"
                            checked={selectedSkills.length === tasks.length - 1}
                            onChange={() => handleSkillToggle('Select All')}
                            className="rounded"
                          />
                          Select All
                        </label>
                      </div>
                      {tasks.filter((t) => t !== 'Other').map((task) => (
                        <label key={task} className="flex items-center px-4 py-2 hover:bg-gray-800 cursor-pointer">
                          <input
                            type="checkbox"
                            checked={selectedSkills.includes(task)}
                            onChange={() => handleSkillToggle(task)}
                            className="mr-2 rounded"
                          />
                          {task}
                        </label>
                      ))}
                    </div>
                  )}
                </div>
              </div>

              <button className="bg-black text-white px-8 py-3 rounded-lg w-32 text-lg font-medium" onClick={() => setShowListings(true)}>
                Find Tasks
              </button>

              {showListings && (
                <div className="mt-8 space-y-4">
                  {getFilteredListings().length > 0 ? (
                    getFilteredListings().map((task) => <TaskListing key={task.id} task={task} />)
                  ) : (
                    <div className="text-center py-8 bg-white rounded-xl shadow-sm">
                      <p className="text-gray-500">No tasks in your area, check back soon!</p>
                    </div>
                  )}
                </div>
              )}
            </div>

            {!showListings && (
              <div className="text-center">
                <h2 className="text-2xl font-bold">
                  100% COMMISSIONS
                  <br />
                  FOR 3 MONTHS
                </h2>
                <p className="text-sm mt-2">+ Early Access: Pre-Orders Only</p>
              </div>
            )}
          </div>
        )}

        {page === 'membership' && (
          <div className="min-h-screen bg-gradient-to-b from-blue-50 via-white to-gray-50 py-16 px-4">
            <div className="max-w-6xl mx-auto">
              <div className="text-center mb-16">
                <h1 className="text-3xl md:text-5xl font-bold mb-4 bg-gradient-to-r from-blue-600 to-purple-600 bg-clip-text text-transparent">
                  Choose Your Path to Success
                </h1>
                <p className="text-xl text-gray-600">Join thousands of successful task professionals</p>
              </div>

              <div className="grid md:grid-cols-3 gap-8 mb-16">
                {/* Task Poster (Free) Plan */}
                <div className="bg-white rounded-2xl shadow-xl overflow-hidden transform hover:scale-105 transition-transform duration-300">
                  <div className="p-8">
                    <div className="flex justify-between items-center mb-4">
                      <h2 className="text-2xl font-bold">Task Poster</h2>
                      <span className="px-3 py-1 bg-gradient-to-r from-gray-100 to-gray-200 text-gray-700 rounded-full text-
sm font-semibold">
                        Free
                      </span>
                    </div>
                    <p className="text-gray-600 mb-6">Perfect for those who need tasks completed</p>
                    <div className="space-y-4">
                      <div className="flex items-center gap-3">
                        <Check className="text-green-500 w-5 h-5" />
                        <span>Post unlimited tasks</span>
                      </div>
                      <div className="flex items-center gap-3">
                        <Store className="text-blue-500 w-5 h-5" />
                        <span>Professional dashboard</span>
                      </div>
                      <div className="flex items-center gap-3">
                        <Zap className="text-yellow-500 w-5 h-5" />
                        <span>Instant payments</span>
                      </div>
                    </div>
                    <button className="bg-blue-500 text-white px-6 py-3 rounded-lg mt-6 hover:bg-blue-600 transition-colors">
                      Sign Up
                    </button>
                  </div>
                </div>

                {/* Task Professional (Premium) Plan */}
                <div className="bg-white rounded-2xl shadow-xl overflow-hidden transform hover:scale-105 transition-transform duration-300">
                  <div className="p-8">
                    <div className="flex justify-between items-center mb-4">
                      <h2 className="text-2xl font-bold">Task Professional</h2>
                      <span className="px-3 py-1 bg-gradient-to-r from-blue-100 to-blue-200 text-blue-700 rounded-full text-sm font-semibold">
                        Premium
                      </span>
                    </div>
                    <p className="text-gray-600 mb-6">Unlock exclusive perks and earn more</p>
                    <div className="space-y-4">
                      {premiumPerks.map(perk => (
                        <div key={perk.id} className="flex items-center gap-3">
                          <perk.icon className="text-blue-500 w-5 h-5" />
                          <span>{perk.name}</span>
                        </div>
                      ))}
                    </div>
                    <button className="bg-purple-500 text-white px-6 py-3 rounded-lg mt-6 hover:bg-purple-600 transition-colors">
                      Subscribe Now
                    </button>
                  </div>
                </div>

                {/* Task Pro (Enterprise) Plan */}
                <div className="bg-white rounded-2xl shadow-xl overflow-hidden transform hover:scale-105 transition-transform duration-300">
                  <div className="p-8">
                    <div className="flex justify-between items-center mb-4">
                      <h2 className="text-2xl font-bold">Task Pro</h2>
                      <span className="px-3 py-1 bg-gradient-to-r from-purple-100 to-purple-200 text-purple-700 rounded-full text-sm font-semibold">
                        Enterprise
                      </span>
                    </div>
                    <p className="text-gray-600 mb-6">For large businesses and teams</p>
                    <div className="space-y-4">
                      <div className="flex items-center gap-3">
                        <Check className="text-green-500 w-5 h-5" />
                        <span>Unlimited tasks</span>
                      </div>
                      <div className="flex items-center gap-3">
                        <Check className="text-green-500 w-5 h-5" />
                        <span>Dedicated account manager</span>
                      </div>
                      <div className="flex items-center gap-3">
                        <Check className="text-green-500 w-5 h-5" />
                        <span>Customizable pricing</span>
                      </div>
                    </div>
                    <button className="bg-yellow-500 text-white px-6 py-3 rounded-lg mt-6 hover:bg-yellow-600 transition-colors">
                      Contact Us
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}
      </main>
    </div>
  )
}